import CloseIcon from '@mui/icons-material/Close'
import {
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import React, { useEffect, useState } from 'react'
import { PaymentRow, PaymentRowType, ScheduleType } from '../../shared/types'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { FeatureFlagName } from '../../../api/feature-flags-api'
import { getRowPayoutValue } from './conversion-utils'
import './payment-schedule-row.scss'

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
interface PaymentScheduleRowProps {
  readonly row: PaymentRow
  readonly rowIndex: number
  readonly scheduleType: ScheduleType
  readonly totalRows: number
  readonly touched: {
    payoutPercentage: boolean
    paymentDate: boolean
  }
  readonly hoverDeleteRow: number | null
  readonly canDelete: boolean
  readonly onPayoutPercentageChange: (index: number, value: string) => void
  readonly onPaymentDateChange: (
    index: number,
    date: Date | string | null
  ) => void
  readonly onTogglePaymentType?: (index: number) => void
  readonly onDeleteRow: (index: number) => void
  readonly onMouseEnterDelete: (index: number) => void
  readonly onMouseLeaveDelete: () => void
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export default function PaymentScheduleRow({
  row,
  rowIndex,
  scheduleType,
  totalRows,
  touched,
  hoverDeleteRow,
  canDelete,
  onPayoutPercentageChange,
  onPaymentDateChange,
  onTogglePaymentType,
  onDeleteRow,
  onMouseEnterDelete,
  onMouseLeaveDelete,
}: Readonly<PaymentScheduleRowProps>) {
  const { flags } = useFeatureFlags()
  const enablePartialMaturitiesByDollarAmount =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT]

  const rowClassName = `payment-schedule-row ${
    hoverDeleteRow === rowIndex ? 'payment-schedule-row--hover-delete' : ''
  }`

  const isLastRow = rowIndex === totalRows - 1
  const isBookValue = scheduleType === ScheduleType.BOOK_VALUE
  const isLockedRow = isLastRow && isBookValue

  const isDollarMode =
    enablePartialMaturitiesByDollarAmount &&
    (row.inputType ?? PaymentRowType.PERCENTAGE) === PaymentRowType.DOLLAR

  // Local display string for dollar mode to support comma formatting
  const [dollarDisplay, setDollarDisplay] = useState<string>(() => {
    const amt = row.payoutAmount
    if (amt === 0 || amt == null) return ''
    return amt.toLocaleString('en-US')
  })

  // Sync local display when the parent value changes externally (e.g. toggle, delete)
  useEffect(() => {
    if (isDollarMode) {
      const amt = row.payoutAmount
      if (amt === 0 || amt == null) {
        setDollarDisplay('')
      } else {
        setDollarDisplay((prev) => {
          if (parseFloat(prev.replace(/,/g, '')) !== amt) {
            return amt.toLocaleString('en-US')
          }
          return prev
        })
      }
    }
  }, [row.payoutAmount, isDollarMode])

  const handleDollarInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value = e.target.value.replace(/[^0-9.]/g, '')

    // Prevent multiple decimal points
    const decimalCount = (value.match(/\./g) ?? []).length
    if (decimalCount > 1) {
      value = value.substring(0, value.lastIndexOf('.'))
    }

    // Limit to 2 decimal places
    const decimalIndex = value.indexOf('.')
    if (decimalIndex !== -1 && value.length - decimalIndex > 3) {
      value = value.substring(0, decimalIndex + 3)
    }

    setDollarDisplay(value)

    onPayoutPercentageChange(rowIndex, value)
  }

  const handleDollarFocus = () => {
    // Strip commas on focus so the user edits raw digits
    setDollarDisplay((prev) => prev.replace(/,/g, ''))
  }

  const handleDollarBlur = () => {
    const raw = dollarDisplay.replace(/,/g, '')
    if (raw !== '' && !isNaN(parseFloat(raw))) {
      const parts = raw.split('.')
      const integerPart = parseInt(parts[0]).toLocaleString('en-US')
      const formattedValue =
        parts[1] !== undefined ? `${integerPart}.${parts[1]}` : integerPart
      setDollarDisplay(formattedValue)
    }
  }

  return (
    <Grid container alignItems="flex-start" className={rowClassName}>
      {enablePartialMaturitiesByDollarAmount && (
        <Grid
          size={{ md: 2 }}
          sx={{
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Switch
            size="small"
            checked={
              (row.inputType ?? PaymentRowType.PERCENTAGE) ===
              PaymentRowType.DOLLAR
            }
            onChange={() => onTogglePaymentType?.(rowIndex)}
            disabled={isLockedRow}
          />
        </Grid>
      )}
      <Grid size={{ md: 5 }} className="payment-schedule-row__payout-field">
        <TextField
          size="small"
          type={isDollarMode ? 'text' : 'number'}
          value={(() => {
            if (isDollarMode) return dollarDisplay
            const amount = getRowPayoutValue(
              row,
              enablePartialMaturitiesByDollarAmount
            )
            return amount === 0 ? '' : amount
          })()}
          onChange={
            isDollarMode
              ? handleDollarInputChange
              : (e) => onPayoutPercentageChange(rowIndex, e.target.value)
          }
          onFocus={isDollarMode ? handleDollarFocus : undefined}
          onBlur={isDollarMode ? handleDollarBlur : undefined}
          disabled={isLockedRow}
          required
          error={(() => {
            const amount = getRowPayoutValue(
              row,
              enablePartialMaturitiesByDollarAmount
            )
            const isPercentageMode =
              (row.inputType ?? PaymentRowType.PERCENTAGE) ===
              PaymentRowType.PERCENTAGE
            return (
              amount === null ||
              amount === 0 ||
              amount < 0 ||
              (isPercentageMode && amount > 100)
            )
          })()}
          helperText={(() => {
            const amount = getRowPayoutValue(
              row,
              enablePartialMaturitiesByDollarAmount
            )
            const isPercentageMode =
              (row.inputType ?? PaymentRowType.PERCENTAGE) ===
              PaymentRowType.PERCENTAGE
            if (amount === null || amount === 0) {
              return touched.payoutPercentage ? 'Required' : ''
            }
            if (touched.payoutPercentage && amount < 0) {
              return 'Must be greater than 0'
            }
            if (touched.payoutPercentage && isPercentageMode && amount > 100) {
              return 'Must be 100% or less'
            }
            return ''
          })()}
          slotProps={{
            htmlInput: isDollarMode
              ? { inputMode: 'numeric' as const }
              : {
                  step: '0.01',
                  min: 0.01,
                  max:
                    (row.inputType ?? PaymentRowType.PERCENTAGE) ===
                    PaymentRowType.PERCENTAGE
                      ? 100
                      : undefined,
                },
            input: {
              ...(isDollarMode
                ? {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }
                : {
                    endAdornment: (
                      <InputAdornment position="end">
                        {(row.inputType ?? PaymentRowType.PERCENTAGE) ===
                        PaymentRowType.PERCENTAGE
                          ? '%'
                          : '$'}
                      </InputAdornment>
                    ),
                  }),
            },
          }}
        />
      </Grid>
      <Grid
        size={{ md: enablePartialMaturitiesByDollarAmount ? 4 : 6 }}
        className="payment-schedule-row__date-field"
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={
              row.paymentDate != null
                ? row.paymentDate instanceof Date
                  ? row.paymentDate
                  : new Date(row.paymentDate)
                : null
            }
            onChange={(date) => onPaymentDateChange(rowIndex, date)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                label: null,
                required: true,
                error: row.paymentDate == null,
                helperText:
                  touched.paymentDate && row.paymentDate == null
                    ? 'Required'
                    : '',
              },
            }}
            shouldDisableDate={(date) => {
              // Disable past dates
              const today = new Date()
              today.setHours(0, 0, 0, 0)
              return date < today
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid size={{ md: 1 }} className="payment-schedule-row__delete-button">
        <IconButton
          size="small"
          onClick={() => onDeleteRow(rowIndex)}
          disabled={!canDelete}
          onMouseEnter={() => onMouseEnterDelete(rowIndex)}
          onMouseLeave={onMouseLeaveDelete}
          className={
            hoverDeleteRow === rowIndex
              ? 'MuiIconButton-root--hover-active'
              : ''
          }
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  )
}
